import type { OddsMove } from '@tennis-scanner/shared';
import {
  BaselineSeries,
  ColorType,
  CrosshairMode,
  type IChartApi,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  LineSeries,
  LineStyle,
  type SeriesMarker,
  type UTCTimestamp,
  createChart,
  createSeriesMarkers,
} from 'lightweight-charts';
import { useEffect, useRef } from 'react';

// Pure chart canvas — owns the lightweight-charts instance lifecycle and
// nothing else. The parent (`odds-trading-chart.tsx`) handles tRPC, state,
// and series-data computation, then feeds plain arrays here.
//
// Why split: keeping all chart primitives in one file lets us swap
// libraries later (TradingView → Recharts, etc.) by replacing only this
// file. No tRPC / business logic leaks down here.

const PICK_COLOR = '#22c55e'; // green
const RIVAL_COLOR = '#9ca3af'; // gray
const DROP_MARKER_COLOR = '#ef4444'; // red — odds went DOWN
const RISE_MARKER_COLOR = '#3b82f6'; // blue — odds went UP
const MATCH_START_COLOR = '#a855f7'; // purple — distinct from move markers

export type LineDatum = { time: UTCTimestamp; value: number };

export interface ChartCanvasProps {
  p1Data: LineDatum[];
  p2Data: LineDatum[];
  /** Pick side determines which series gets the BaselineSeries (value
      shading). Without a pick, both sides render as plain lines in muted
      colors. */
  pickSide: 'p1' | 'p2' | undefined;
  /** Cuota mínima — anchors the baseline + target line. Required when
      pickSide is set. */
  targetOdds: number | undefined;
  p1Label: string;
  p2Label: string;
  /** Server-detected V2 moves — drops AND rises. Plotted as direction-coded
      markers at `since` (peak for drops, trough for rises). */
  moveP1: OddsMove | null;
  moveP2: OddsMove | null;
  /** Match scheduled start (ISO). Plotted as a purple square so the
      operator can read pre-match vs in-play movement. */
  matchStartsAt: string | undefined;
}

interface ChartHandles {
  chart: IChartApi;
  pickSeries: ISeriesApi<'Line'> | ISeriesApi<'Baseline'>;
  rivalSeries: ISeriesApi<'Line'>;
  // Marker primitives — created ONCE per series and updated via .setMarkers()
  // afterwards. Calling createSeriesMarkers() repeatedly leaks plugin
  // instances (v5 attaches a new primitive each call), so we hold the
  // returned handle and reuse it.
  pickMarkers: ISeriesMarkersPluginApi<UTCTimestamp>;
  rivalMarkers: ISeriesMarkersPluginApi<UTCTimestamp>;
  pickIsP1: boolean;
}

export function OddsTradingChartCanvas(props: ChartCanvasProps) {
  const { pickSide, targetOdds, p1Label, p2Label, p1Data, p2Data, moveP1, moveP2, matchStartsAt } =
    props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const handlesRef = useRef<ChartHandles | null>(null);

  // Mount + rebuild when chart-level options change (pickSide swap, target
  // change, label change). Data updates run in a separate effect below.
  useEffect(() => {
    if (!containerRef.current) return;
    const handles = buildChart(containerRef.current, {
      pickSide,
      targetOdds,
      p1Label,
      p2Label,
    });
    handlesRef.current = handles;
    return () => {
      handles.chart.remove();
      handlesRef.current = null;
    };
  }, [pickSide, targetOdds, p1Label, p2Label]);

  // Data updates run on every prop change — cheap at 5s × ~720 points.
  useEffect(() => {
    const h = handlesRef.current;
    if (!h) return;
    const pickData = h.pickIsP1 ? p1Data : p2Data;
    const rivalData = h.pickIsP1 ? p2Data : p1Data;
    h.pickSeries.setData(pickData);
    h.rivalSeries.setData(rivalData);
  }, [p1Data, p2Data]);

  // Markers — recomputed when move data or matchStartsAt changes. Markers
  // are attached to a series; moves go on the side that moved, and the
  // match-start marker goes on the pick side (most relevant context).
  useEffect(() => {
    const h = handlesRef.current;
    if (!h) return;
    const pickMarkers: SeriesMarker<UTCTimestamp>[] = [];
    const rivalMarkers: SeriesMarker<UTCTimestamp>[] = [];

    const pickMove = h.pickIsP1 ? moveP1 : moveP2;
    const rivalMove = h.pickIsP1 ? moveP2 : moveP1;
    if (pickMove) pickMarkers.push(toMoveMarker(pickMove));
    if (rivalMove) rivalMarkers.push(toMoveMarker(rivalMove));

    if (matchStartsAt) {
      const t = isoToUtcTimestamp(matchStartsAt);
      if (t !== null) {
        pickMarkers.push({
          time: t,
          position: 'inBar',
          color: MATCH_START_COLOR,
          shape: 'square',
          text: '🎾 inicio',
          size: 1,
        });
      }
    }

    // setMarkers() on the handles created at mount — no new primitive per
    // update. (Calling createSeriesMarkers() here would leak plugins.)
    h.pickMarkers.setMarkers(pickMarkers);
    h.rivalMarkers.setMarkers(rivalMarkers);
  }, [moveP1, moveP2, matchStartsAt]);

  return <div ref={containerRef} className="h-[420px] w-full" />;
}

interface BuildChartOpts {
  pickSide: ChartCanvasProps['pickSide'];
  targetOdds: ChartCanvasProps['targetOdds'];
  p1Label: string;
  p2Label: string;
}

function buildChart(el: HTMLDivElement, props: BuildChartOpts): ChartHandles {
  const chart = createChart(el, {
    autoSize: true,
    layout: {
      background: { type: ColorType.Solid, color: 'transparent' },
      textColor: '#9ca3af',
      fontFamily: 'JetBrains Mono Variable, monospace',
      panes: {
        separatorColor: 'rgba(255,255,255,0.08)',
        separatorHoverColor: 'rgba(255,255,255,0.18)',
        enableResize: true,
      },
    },
    grid: {
      vertLines: { color: 'rgba(255,255,255,0.04)' },
      horzLines: { color: 'rgba(255,255,255,0.06)' },
    },
    rightPriceScale: { borderColor: 'rgba(255,255,255,0.1)' },
    timeScale: {
      borderColor: 'rgba(255,255,255,0.1)',
      timeVisible: true,
      secondsVisible: false,
    },
    crosshair: { mode: CrosshairMode.Magnet },
  });

  const pickIsP1 = props.pickSide !== 'p2';
  const pickLabel = pickIsP1 ? props.p1Label : props.p2Label;
  const rivalLabel = pickIsP1 ? props.p2Label : props.p1Label;

  // Two stacked panes — one per side. Pick on top (60% height), rival on
  // bottom (40%). Each pane auto-scales so two odds hundreds apart from
  // each other (e.g. 1.62 vs 3.09) don't both look flat on a shared axis.
  const PICK_PANE = 0;
  const RIVAL_PANE = 1;

  // Pick side: BaselineSeries when we have a target — shades green when
  // live > target so the operator sees "value time" at a glance. Falls
  // back to a plain line when no target (cockpit opened without a pick).
  const pickSeries: ISeriesApi<'Line'> | ISeriesApi<'Baseline'> =
    props.targetOdds !== undefined && props.pickSide !== undefined
      ? chart.addSeries(
          BaselineSeries,
          {
            baseValue: { type: 'price', price: props.targetOdds },
            topLineColor: PICK_COLOR,
            topFillColor1: 'rgba(34, 197, 94, 0.55)',
            topFillColor2: 'rgba(34, 197, 94, 0.15)',
            bottomLineColor: '#ef4444',
            bottomFillColor1: 'rgba(239, 68, 68, 0.08)',
            bottomFillColor2: 'rgba(239, 68, 68, 0.0)',
            lineWidth: 2,
            title: pickLabel,
          },
          PICK_PANE,
        )
      : chart.addSeries(
          LineSeries,
          { color: PICK_COLOR, lineWidth: 2, title: pickLabel },
          PICK_PANE,
        );

  const rivalSeries = chart.addSeries(
    LineSeries,
    { color: RIVAL_COLOR, lineWidth: 2, title: rivalLabel },
    RIVAL_PANE,
  );

  if (props.targetOdds !== undefined) {
    pickSeries.createPriceLine({
      price: props.targetOdds,
      color: PICK_COLOR,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: true,
      title: `target ${props.targetOdds.toFixed(3)}`,
    });
  }

  // Pane heights: pick gets 60%, rival 40%. Calling setHeight on the pick
  // pane is enough — lightweight-charts redistributes the remainder.
  const panes = chart.panes();
  const totalHeight = el.clientHeight || 320;
  if (panes[PICK_PANE]) panes[PICK_PANE].setHeight(Math.round(totalHeight * 0.6));

  // Pre-create the marker primitives empty. The data effect updates them
  // via setMarkers() — calling createSeriesMarkers() per-update would
  // leak plugin instances (each call attaches a new primitive).
  // Cast to UTCTimestamp variant: our series only ever uses intraday
  // numeric timestamps, never BusinessDay strings.
  const pickMarkers = createSeriesMarkers(pickSeries, []) as ISeriesMarkersPluginApi<UTCTimestamp>;
  const rivalMarkers = createSeriesMarkers(
    rivalSeries,
    [],
  ) as ISeriesMarkersPluginApi<UTCTimestamp>;

  return { chart, pickSeries, rivalSeries, pickMarkers, rivalMarkers, pickIsP1 };
}

// Drop = red arrowDown above the bar; rise = blue arrowUp below the bar.
// The marker is anchored at `since` (the START of the move = the extreme
// before the reversal): peak for drops, trough for rises.
function toMoveMarker(move: OddsMove): SeriesMarker<UTCTimestamp> {
  const t = isoToUtcTimestamp(move.since);
  const fallback = Math.floor(Date.now() / 1000) as UTCTimestamp;
  const isDrop = move.direction === 'drop';
  return {
    time: (t ?? fallback) as UTCTimestamp,
    position: isDrop ? 'aboveBar' : 'belowBar',
    color: isDrop ? DROP_MARKER_COLOR : RISE_MARKER_COLOR,
    shape: isDrop ? 'arrowDown' : 'arrowUp',
    text: `${isDrop ? 'drop' : 'rise'} ${move.percentChange.toFixed(1)}% · ${move.book}`,
    size: 1,
  };
}

function isoToUtcTimestamp(iso: string): UTCTimestamp | null {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000) as UTCTimestamp;
}
